import { defineStore } from "pinia";
import i18n from "@/Plugins/i18n";

import type {
  Component,
  RegisterData,
  RegisterB2bData,
  Step,
  StepSource,
  Choice,
  B2bUser,
} from "@/Modules/Models/Domain/OnBoarding";
import type { State } from "@/Modules/OnBoarding/Models/State";
import { Language } from "@/Common/Models/Domain/Language";
import { StateNamespace } from "@/Modules/App/Models/State/StateNamespace";
import { LocalStorageNamespace } from "@/Modules/App/Models/Localstorage/LocalStorageNamespace";
import { LocalStorageKey } from "@/Modules/App/Models/Localstorage/LocalStorageKey";
import {
  AnswersIds,
  StepId,
  TherapyType,
  AdditionalTherapyLanguage,
  StepIdWithChoices,
  AREA_EXTRA_THERAPIST_KNOW,
  SUBAREA_NAME_THERAPIST_KNOWN,
  OTHER_LOCATION,
  EMPTY_AREA,
} from "@/Modules/Models/Domain/OnBoarding";
import { Source } from "@/Common/Models/Domain/Source";
import type { Composer } from "vue-i18n";

import { config } from "@/Common/envConfig";

import SetCurrentStepActionHandler from "@/Modules/OnBoarding/ActionHandlers/SetCurrentStep";
import SetAnswerActionHandler from "@/Modules/OnBoarding/ActionHandlers/SetAnswer";
import SetOptionsDataActionHandler from "@/Modules/OnBoarding/ActionHandlers/SetOptionsData";
import RegisterUser from "@/Modules/OnBoarding/ActionHandlers/RegisterUser";
import ResendEmailActionHandler from "@/Modules/OnBoarding/ActionHandlers/ResendEmail";
import SendCodeActionHandler from "@/Modules/OnBoarding/ActionHandlers/SendCode";
import SetGTMEventActionHandler from "@/Modules/OnBoarding/ActionHandlers/SetGTMEvent";
import UpdateUserRegistration from "@/Modules/OnBoarding/ActionHandlers/UpdateUserRegistration";
import GoToPoolActionHandler from "@/Modules/OnBoarding/ActionHandlers/GoToPool";
import updateUserRegistrationAH from "./ActionHandlers/UpdateUserSSORegistration";
import registerB2bUserAH from "./ActionHandlers/RegisterB2bUser";
import updateReassignUserAH from "./ActionHandlers/UpdateReassignUser";

import { stepMap, stepSource } from "@/Modules/OnBoarding/Data/GeneralStepSource";
import {
  patientComeBackStepMap,
  patientComeBackstepSource,
} from "@/Modules/OnBoarding/Data/PatientComeBackStepSource";
import { SSOPatientStepMap, SSOPatientStepSource } from "@/Modules/OnBoarding/Data/SSOPatientSource";
import { reassignStepMap, reassignStepSource } from "@/Modules/OnBoarding/Data/ReassignStepSource";

import { LocalStorageService } from "@/Services/LocalStorageService";
import useUserStore from "@/Modules/User/Store";

import { isValidString } from "@/Utils/String";
import { hasValue } from "@/Utils/Objects";
import { isValidLanguage, navigatorLang } from "@/Utils/Language";

const onBoardingState: State = {
  stepSource: stepSource,
  currentStep: StepId.WELCOME,
  historicalSteps: [StepId.WELCOME],
  isTrial: false,
  username: "",
  email: "",
  areaSelected: "",
  stepMap: stepMap,
  answers: {},
  errorCode: null,
  hasErrorInProcess: false,
  isLoading: false,
  isDepressionSelected: false,
  sessionUuid: "",
  isGtmReturning: false,
  patientWantsLastTherapist: false,
  isUpdateRegister: false,
  isOnboardingCanceled: false,
  b2bUser: null,
  locations: [],
  companySections: [],
  version: config.onboardingVersion,
  timeZones: [],
  deleteAccountReasons: [],
  refundReasons: [],
  dischargeReasons: [],
  manualSessionReasons: [],
  breakReasons: [],
};

let gtmTimeOut: undefined | ReturnType<typeof setTimeout>;

export const useOnBoardingStore = defineStore(StateNamespace.ON_BOARDING, {
  state: () => ({ ...onBoardingState }),

  actions: {
    setNextStep(component: Component | null): void {
      const setCurrentStepActionHandler = new SetCurrentStepActionHandler(this);

      setCurrentStepActionHandler.handle(component, this.currentStep);

      this.saveStateInLocalStorage();
    },

    setLastStep(): void {
      const lastStep = this.historicalSteps.pop();

      this.currentStep = lastStep ? lastStep : StepId.WELCOME;
    },

    setAnswer(answerId: AnswersIds | string, value: string | number | boolean): void {
      const setAnswerActionHandler = new SetAnswerActionHandler(this);

      setAnswerActionHandler.handle(answerId, value);
    },

    async setOptionsData(language: Language): Promise<void> {
      const setOptionsDataActionHandler = new SetOptionsDataActionHandler(this);

      await setOptionsDataActionHandler.handle(language);
    },

    setNewReturningGtmEvent(): void {
      const timeToSet = 30; // in minutes

      if (gtmTimeOut) {
        clearTimeout(gtmTimeOut);
      }

      gtmTimeOut = setTimeout(
        () => {
          this.isGtmReturning = true;
        },
        timeToSet * 60 * 1000
      );
    },

    sendGTMEvent(data: {
      language: string;
      component?: Component;
      registerData?: RegisterData;
      eventStep?: string;
      planId?: string;
    }): void {
      this.setNewReturningGtmEvent();

      const setGTMEventActionHandler = new SetGTMEventActionHandler(this);

      setGTMEventActionHandler.handle(data);
    },

    saveStateInLocalStorage(): void {
      const localStorageService: LocalStorageService = new LocalStorageService(
        LocalStorageNamespace.ON_BOARDING
      );
      const onBoardingToSave = {
        ...this.$state,
        isLoading: onBoardingState.isLoading,
        errorCode: null,
        hasErrorInProcess: false,
      };
      localStorageService.set<State>(LocalStorageKey.ON_BOARDING_DATA, onBoardingToSave);
    },

    recoverStateFromLocalStorage(): void {
      const localStorageService: LocalStorageService = new LocalStorageService(
        LocalStorageNamespace.ON_BOARDING
      );

      const state = localStorageService.get<State>(LocalStorageKey.ON_BOARDING_DATA);

      if (state) {
        this.$state = {
          ...this.$state,
          ...state,
        };
        this.isGtmReturning = true;

        const lang = isValidLanguage(state.answers[AnswersIds.LANGUAGE])
          ? state.answers[AnswersIds.LANGUAGE]
          : ((i18n.global as unknown as Composer).locale.value ?? navigatorLang);

        this.updateLanguage(lang);
      }
    },

    clearLocalStorageState(): void {
      const localStorageService: LocalStorageService = new LocalStorageService(
        LocalStorageNamespace.ON_BOARDING
      );

      localStorageService.set<null>(LocalStorageKey.ON_BOARDING_DATA, null);
    },

    resetOnboardingStore(): void {
      let resettedStepSourceValues = {};

      Object.entries(this.stepSource).forEach(([key, step]) => {
        resettedStepSourceValues = {
          ...resettedStepSourceValues,
          [key]: {
            ...step,
            components: step.components.map((component) => ({
              ...component,
              value: "",
            })),
          },
        };
      });

      this.$state = {
        stepSource: resettedStepSourceValues as StepSource,
        currentStep: StepId.WELCOME,
        historicalSteps: [StepId.WELCOME],
        isTrial: false,
        username: "",
        email: "",
        areaSelected: "",
        stepMap: stepMap,
        answers: {},
        errorCode: null,
        hasErrorInProcess: false,
        isLoading: false,
        isDepressionSelected: false,
        sessionUuid: "",
        isGtmReturning: false,
        patientWantsLastTherapist: false,
        isUpdateRegister: false,
        isOnboardingCanceled: false,
        b2bUser: null,
        locations: [],
        companySections: [],
        timeZones: [],
        deleteAccountReasons: [],
        refundReasons: [],
        dischargeReasons: [],
        manualSessionReasons: [],
        breakReasons: [],
      };
    },

    async registerUser(registerData: RegisterData, language: string): Promise<void> {
      const registerUserActionHandler = new RegisterUser(this);

      await registerUserActionHandler.handle(registerData, language);

      Object.keys(registerData).map((answerId) => {
        if (registerData[answerId as keyof typeof registerData]) {
          this.setAnswer(answerId, registerData[answerId as keyof typeof registerData]);
        }
      });
    },

    async registerB2bUser(registerB2bData: RegisterB2bData): Promise<void> {
      await registerB2bUserAH(registerB2bData);
    },

    async resendEmail(language: string): Promise<void> {
      const resendEmailActionHandler = new ResendEmailActionHandler(this);

      await resendEmailActionHandler.handle(language);
    },

    async sendCode(code: string, language: string): Promise<void> {
      const sendCodeActionHandler = new SendCodeActionHandler(this);

      await sendCodeActionHandler.handle(code, language);
    },

    resetError(): void {
      this.errorCode = null;
      this.hasErrorInProcess = false;
    },

    launchStorePatientComeBack() {
      this.stepSource = patientComeBackstepSource;
      this.stepMap = patientComeBackStepMap;
      this.currentStep = StepId.WELCOME_AGAIN;
      this.historicalSteps = [StepId.WELCOME_AGAIN];
      this.recoverStateFromLocalStorage();
    },

    setCurrentStep(step: StepId) {
      this.currentStep = step;
    },

    async updateUserComeback() {
      const updateUseRegistration = new UpdateUserRegistration(this);

      await updateUseRegistration.handle();
    },

    async goToPool(lastTherapistUuid?: string, therapistNameSelected?: string) {
      const goToPoolActionHandler = new GoToPoolActionHandler(this);

      await goToPoolActionHandler.handle(lastTherapistUuid, therapistNameSelected);
    },

    async updateLanguage(language?: string) {
      const userStore = useUserStore();
      const lang = isValidLanguage(language)
        ? language
        : isValidLanguage(userStore.user?.language)
          ? userStore.user.language
          : navigatorLang;

      userStore.updateLanguage(lang);

      await this.setOptionsData(lang);
    },

    setB2BData(b2bData: B2bUser | null) {
      this.b2bUser = b2bData;

      if (hasValue(this.b2bUser)) this.setAnswer(AnswersIds.LANGUAGE, this.b2bUser.language);
    },

    resetTherapyType() {
      if (!hasValue(this.stepSource[StepId.THERAPY_TYPE])) return;

      delete this.answers[AnswersIds.THERAPY_TYPE];

      if (this.stepSource[StepId.THERAPY_TYPE].components.length > 0) {
        this.stepSource[StepId.THERAPY_TYPE].components[0]!.value = "";
      }
    },

    resetTherapyLanguage() {
      if (!hasValue(this.stepSource[StepId.THERAPY_LANGUAGE])) return;

      delete this.answers[AnswersIds.THERAPY_LANGUAGE];

      if (this.stepSource[StepId.THERAPY_LANGUAGE].components.length > 0) {
        this.stepSource[StepId.THERAPY_LANGUAGE].components[0]!.value = "";
      }
    },

    launchOnBoardingSSOPatient() {
      this.updateLanguage();
      this.answers.therapy_type = TherapyType.SINGLE;
      this.stepSource = SSOPatientStepSource;
      this.stepMap = SSOPatientStepMap;
      this.currentStep = StepId.WELCOME_B2B;
      this.historicalSteps = [StepId.WELCOME_B2B];
      this.recoverStateFromLocalStorage();
    },

    launchOnBoardingB2BPatient() {
      this.stepSource = stepSource;
      this.stepMap = stepMap;
      this.currentStep = StepId.WELCOME_B2B;
      this.historicalSteps = [StepId.WELCOME_B2B];
      this.recoverStateFromLocalStorage();
    },

    launchOnBoardingReassignPatient() {
      this.updateLanguage();
      this.stepSource = reassignStepSource;
      this.stepMap = reassignStepMap;
      this.currentStep = StepId.WELCOME_REASSIGN;
      this.historicalSteps = [StepId.WELCOME_REASSIGN];
      this.recoverStateFromLocalStorage();
    },

    async updateUserSSO() {
      await updateUserRegistrationAH();
    },

    async updateReassignUser(): Promise<void> {
      await updateReassignUserAH();
    },
  },

  getters: {
    getStep(state: State): Step {
      // @ts-expect-error Error indexing step source
      return state.stepSource[state.currentStep];
    },

    getNumberStep(state: State): { total: number; current: number } {
      const steps = Object.keys(state.stepMap);
      const totalSteps = steps.length;
      const current = steps.indexOf(state.currentStep) - 1;

      return {
        total: totalSteps,
        current,
      };
    },

    getStepChoices(state: State): (step: StepIdWithChoices) => Choice[] | [] {
      return (step: StepIdWithChoices): Choice[] => {
        const components = state.stepSource[step].components;

        if (components.length > 0) {
          const choices = components[0]!.choices;

          if (typeof choices === "object") return choices;
        }

        return [];
      };
    },

    getChoiceInfo(): (step: StepIdWithChoices, filter: string, field: keyof Choice) => Choice | undefined {
      return (step: StepIdWithChoices, filter: string, field: keyof Choice): Choice | undefined => {
        const content = this.getStepChoices(step);

        return content.find((element) => isValidString(filter) && element[field] === filter);
      };
    },

    isTherapistKnowSelected(state: State): boolean {
      const area = this.getChoiceInfo(StepIdWithChoices.MAIN_AREA, state.areaSelected, "uuid");
      const subareaSelected = this.isCoupleSelected ? (state.answers.sub_area as string) : state.areaSelected;
      const subarea = this.getChoiceInfo(StepIdWithChoices.SUB_AREA, subareaSelected as string, "uuid");

      return area?.name === AREA_EXTRA_THERAPIST_KNOW || subarea?.name === SUBAREA_NAME_THERAPIST_KNOWN;
    },

    isCoachingSelected(state: State): boolean {
      return state.answers.therapy_type === TherapyType.COACHING;
    },

    isCoupleSelected(state: State): boolean {
      return state.answers.therapy_type === TherapyType.COUPLE;
    },

    isB2bUser(state: State): boolean {
      return hasValue(state.b2bUser);
    },

    isWellhubUser(state: State): boolean {
      return this.isB2bUser && state.b2bUser?.source === Source.WELLHUB;
    },

    isGympassUser(state: State): boolean {
      return this.isB2bUser && state.b2bUser?.source === Source.GYMPASS;
    },

    isClarianeUser(state: State): boolean {
      return this.isB2bUser && state.b2bUser?.source === Source.CLARAINE;
    },

    isIdealistaUser(state: State): boolean {
      return this.isB2bUser && state.b2bUser?.source === Source.IDEALISTA;
    },

    isCustomB2bUser(): boolean {
      return this.isB2bUser && (this.isClarianeUser || this.isIdealistaUser);
    },

    getAdditionalLanguageSelected(): Choice | undefined {
      if (!isValidString(this.answers.therapy_language)) return;

      const answer = this.answers.therapy_language;

      return this.getChoiceInfo(StepIdWithChoices.THERAPY_LANGUAGE, answer, "uuid");
    },

    isAdditionalLanguageSelected(): boolean {
      const therapyLanguage = this.getAdditionalLanguageSelected;

      return [
        AdditionalTherapyLanguage.CATALAN,
        AdditionalTherapyLanguage.EUSKERA,
        AdditionalTherapyLanguage.GALICIAN,
      ].includes(therapyLanguage?.i18n as AdditionalTherapyLanguage);
    },

    isOtherLocationSelected(state: State): boolean {
      return state.answers.zone === OTHER_LOCATION;
    },

    isEmptyAreaSelected(state: State): boolean {
      const area = this.getChoiceInfo(StepIdWithChoices.MAIN_AREA, state.areaSelected, "uuid");

      return area?.name === EMPTY_AREA;
    },

    isPrivacyPolicyPending(): boolean {
      const userStore = useUserStore();

      return !hasValue(userStore.user?.privacyPolicyAt);
    },
  },
});

export type OnBoardingStore = ReturnType<typeof useOnBoardingStore>;

export default useOnBoardingStore;
